import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Box,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { animate, motion, useMotionValue, type PanInfo } from 'motion/react';
import { BiCheckDouble } from 'react-icons/bi';
import { FiCalendar } from 'react-icons/fi';
import { TodoType } from '@th-types/inbox-reviews.type';
import type { TodoItem } from '@th-types/inbox-reviews.type';
import useAvatarImageFallback from '@hooks/useAvatarImageFallback';

export const TODO_TYPE_LABELS: Record<TodoType, string> = {
  [TodoType.COMPANY_SHARED_JOB]: 'Shared Job',
  [TodoType.RECOMMENDED_JOB]: 'Shared Job',
  [TodoType.COMPANY_OFFERS_JOB]: 'Job Offer',
  [TodoType.COMPANY_OFFERS_SHIFTS]: 'Shift Offer',
  [TodoType.ENTER_HOURS]: 'Enter Hours',
  [TodoType.ENTER_RATING]: 'Review',
  [TodoType.ENTER_RATING_PAYMENT]: 'Review & Payment',
};

const REVEAL_WIDTH = 72;
const OPEN_THRESHOLD = REVEAL_WIDTH / 2;
const DRAG_CLICK_SUPPRESS_PX = 8;

/**
 * Wraps the first occurrence of companyName in the todoMessage with a clickable
 * Anchor to the company page. Falls back to plain text when info is missing.
 */
export function renderTodoMessageWithCompanyLink(
  message: string,
  companyName: string | undefined,
  companyHref: string | undefined
): ReactNode {
  if (!companyName || !companyHref || !message.includes(companyName)) {
    return message;
  }
  const idx = message.indexOf(companyName);
  return (
    <>
      {message.slice(0, idx)}
      <Anchor
        href={companyHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        c="inherit"
        underline="always"
        fw="inherit"
        size="sm"
      >
        {companyName}
      </Anchor>
      {message.slice(idx + companyName.length)}
    </>
  );
}

export interface TodoListItemProps {
  todo: TodoItem;
  onOpen?: (todo: TodoItem) => void;
  companyHref?: string;
  onDismiss?: (todo: TodoItem) => void;
  dismissingId?: number | null;
}

export default function TodoListItem({
  todo,
  onOpen,
  companyHref,
  onDismiss,
  dismissingId,
}: TodoListItemProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  useAvatarImageFallback(imageRef);

  const canDismiss = onDismiss != null && todo.jobRecommendationId != null;
  const isDismissing = dismissingId === todo.jobRecommendationId;

  const x = useMotionValue(0);
  const [isOpen, setIsOpen] = useState(false);
  const dragDistanceRef = useRef(0);

  const snapTo = (open: boolean) => {
    animate(x, open ? -REVEAL_WIDTH : 0, {
      type: 'spring',
      stiffness: 500,
      damping: 40,
    });
    setIsOpen(open);
  };

  const handleDrag = (_event: PointerEvent, info: PanInfo) => {
    dragDistanceRef.current = Math.max(
      dragDistanceRef.current,
      Math.abs(info.offset.x)
    );
  };

  const handleDragEnd = (_event: PointerEvent, info: PanInfo) => {
    const projected = x.get() + info.velocity.x * 0.15;
    snapTo(projected < -OPEN_THRESHOLD);
  };

  const handleContentClick = () => {
    if (dragDistanceRef.current > DRAG_CLICK_SUPPRESS_PX) {
      dragDistanceRef.current = 0;
      return;
    }
    dragDistanceRef.current = 0;

    if (isOpen) {
      snapTo(false);
      return;
    }
    onOpen?.(todo);
  };

  const handleDismiss = (e: MouseEvent) => {
    e.stopPropagation();
    if (!canDismiss || isDismissing) return;
    onDismiss?.(todo);
  };

  const content = (
    <Group gap="sm" align="flex-start" wrap="nowrap" px="sm">
      <Avatar ref={imageRef} src={todo.companyLogoUrl} size={40} radius="xl" />

      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={500} lineClamp={2}>
          {renderTodoMessageWithCompanyLink(
            todo.todoMessage,
            todo.companyName,
            companyHref
          )}
        </Text>
        {todo.eventName && (
          <Group gap={4} wrap="nowrap">
            <FiCalendar
              size={12}
              color="var(--mantine-color-dimmed)"
              style={{ flexShrink: 0 }}
            />
            <Text size="xs" c="dimmed" lineClamp={1}>
              {todo.eventName}
            </Text>
          </Group>
        )}
        <Badge size="sm" variant="light" color="gray">
          {TODO_TYPE_LABELS[todo.type] ?? todo.type}
        </Badge>
      </Stack>

      {canDismiss && (
        <Tooltip label="Dismiss this job">
          <ActionIcon
            variant="transparent"
            color="var(--black)"
            onClick={handleDismiss}
            loading={isDismissing}
            aria-label="Dismiss job"
            style={{ flexShrink: 0 }}
          >
            <BiCheckDouble size={20} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  if (!canDismiss) {
    return (
      <Box onClick={handleContentClick} py="sm" style={{ cursor: 'pointer' }}>
        {content}
      </Box>
    );
  }

  return (
    <Box pos="relative" style={{ overflow: 'hidden' }}>
      <Box
        pos="absolute"
        top={0}
        right={0}
        bottom={0}
        w={REVEAL_WIDTH}
        bg="var(--red)"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <ActionIcon
          variant="transparent"
          c="white"
          size="lg"
          onClick={handleDismiss}
          loading={isDismissing}
          aria-label="Dismiss job"
        >
          <BiCheckDouble size={24} />
        </ActionIcon>
        <Text c="white" size="xs" fw={700}>
          Dismiss
        </Text>
      </Box>

      <motion.div
        drag="x"
        dragConstraints={{ left: -REVEAL_WIDTH, right: 0 }}
        dragElastic={0.12}
        dragDirectionLock
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onClick={handleContentClick}
        style={{
          x,
          position: 'relative',
          background: 'var(--mantine-color-body)',
          cursor: 'pointer',
          paddingTop: 'var(--mantine-spacing-sm)',
          paddingBottom: 'var(--mantine-spacing-sm)',
          touchAction: 'pan-y',
        }}
      >
        {content}
      </motion.div>
    </Box>
  );
}
